import React, { useState, useEffect, memo, useMemo } from "react";
import { Download, Filter, FileText, X } from "lucide-react";
import Payslips from "./Payslips";
import Form16 from "./Form16";
import Filters from "./Filters";
import {
  employeeService,
  branchService,
  departmentService,
  designationService,
  gradesService,
  categoryService,
  subDepartmentService,
} from "../../services/api";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

/**
 * Payroll Reports component that displays and manages:
 * 1. Payslips
 * 2. Form-16
 */
const PAYROLL_REPORTS_FILTERS_KEY = "payrollReports_filters";

function buildApiParamsFromFilters(filtersObj) {
  const params = {};
  if (!filtersObj) return params;
  const mappings = [
    ["branch", "branchId", "number"],
    ["department", "departmentId", "number"],
    ["designation", "designationId", "number"],
    ["grade", "gradeId", "number"],
    ["category", "categoryId", "number"],
    ["subDepartment", "subDepartmentId", "number"],
    ["employmentType", "employmentType", "string"],
    ["status", "employmentStatus", "string"],
    ["pfApplicable", "pfApplicable", "boolean"],
    ["esiApplicable", "esiApplicable", "boolean"],
    ["ptApplicable", "ptApplicable", "boolean"],
    ["gratuityApplicable", "gratuityApplicable", "boolean"],
    ["epsApplicable", "epsApplicable", "boolean"],
    ["ctcFrom", "ctcFrom", "number"],
    ["ctcTo", "ctcTo", "number"],
    ["dateFilterType", "dateFilterType", "string"],
  ];
  mappings.forEach(([key, apiKey, type]) => {
    let val = filtersObj[key];
    if (val === undefined || val === null || val === "") return;
    if (type === "boolean") {
      if (val === true || val === false) params[apiKey] = val;
      else if (val === "true" || val === "false") params[apiKey] = val === "true";
    } else if (type === "number") {
      const n = Number(val);
      if (!Number.isNaN(n)) params[apiKey] = n;
    } else {
      params[apiKey] = typeof val === "string" ? val.trim() : val;
    }
  });
  const dType = filtersObj.dateFilterType;
  const from = filtersObj.reportDateFrom;
  const to = filtersObj.reportDateTo;
  if (dType && dType !== "all") {
    if (from) params.dateFrom = from;
    if (to) params.dateTo = to;
  }
  return params;
}

const defaultFilters = {
  branch: "",
  department: "",
  designation: "",
  grade: "",
  category: "",
  subDepartment: "",
  employmentType: "",
  status: "",
  dateFrom: "",
  dateTo: "",
  reportDateFrom: "",
  reportDateTo: "",
  dateFilterType: "all",
  pfApplicable: "",
  esiApplicable: "",
  ptApplicable: "",
  gratuityApplicable: "",
  epsApplicable: "",
  ctcFrom: "",
  ctcTo: "",
};

const PayrollReports = memo(
  function PayrollReports({
    darkMode,
    setCurrentPage,
    toggleDarkMode,
    forcedSubTab = null,
    onSubTabChange = null,
    hideSidebar = false,
  }) {
    const [activeSubTab, setActiveSubTab] = useState(forcedSubTab || "payslips");
    const [employees, setEmployees] = useState([]);
    const [selectedMonthYear, setSelectedMonthYear] = useState("");
    const [selectedYear, setSelectedYear] = useState("2024-25");
    const [showFilterPopup, setShowFilterPopup] = useState(false);

    // Master data for filters
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [grades, setGrades] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subDepartments, setSubDepartments] = useState([]);

    // Filter states - initialize from localStorage if persisted
    const [filters, setFilters] = useState(() => {
      try {
        const stored = localStorage.getItem(PAYROLL_REPORTS_FILTERS_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          return { ...defaultFilters, ...parsed };
        }
      } catch (e) {
        /* ignore */
      }
      return { ...defaultFilters };
    });

    const [showFilters, setShowFilters] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (!forcedSubTab) return;
      setActiveSubTab(forcedSubTab);
    }, [forcedSubTab]);

    useEffect(() => {
      if (typeof onSubTabChange === "function") onSubTabChange(activeSubTab);
    }, [activeSubTab, onSubTabChange]);

    useEffect(() => {
      fetchMasterData();
    }, []);

    const applyResponseToEmployees = (response) => {
      if (!response) setEmployees([]);
      else if (Array.isArray(response)) setEmployees(response);
      else if (response.data && Array.isArray(response.data)) setEmployees(response.data);
      else if (response.employees && Array.isArray(response.employees)) setEmployees(response.employees);
      else if (response.success && Array.isArray(response.data)) setEmployees(response.data);
      else setEmployees([]);
    };

    // Build API params including payslipMonthYear when on payslips tab
    const getFetchParams = () => {
      const params = buildApiParamsFromFilters(filters);
      const hasActive = !!(
        filters.branch || filters.department || filters.designation || filters.grade ||
        filters.category || filters.subDepartment || filters.employmentType || filters.status ||
        filters.dateFrom || filters.dateTo || filters.reportDateFrom || filters.reportDateTo ||
        (filters.dateFilterType && filters.dateFilterType !== "all") ||
        filters.pfApplicable || filters.esiApplicable || filters.ptApplicable ||
        filters.gratuityApplicable || filters.epsApplicable || filters.ctcFrom || filters.ctcTo
      );
      const baseParams = hasActive ? params : {};
      if (activeSubTab === "payslips" && selectedMonthYear && selectedMonthYear.trim()) {
        baseParams.payslipMonthYear = selectedMonthYear.trim();
      }
      baseParams.excludeEmploymentTypes = "External,External";
      return baseParams;
    };

    // Fetch employees on mount and when filters or selectedMonthYear change
    useEffect(() => {
      const fetchParams = getFetchParams();
      const promise = Object.keys(fetchParams).length > 0
        ? employeeService.getEmployees(fetchParams)
        : employeeService.getEmployees();
      promise
        .then(applyResponseToEmployees)
        .catch((error) => {
          console.error("Error fetching employees:", error);
          toast.error("Failed to fetch employees");
          setEmployees([]);
        });
    }, [filters, activeSubTab, selectedMonthYear]);

    const fetchMasterData = async () => {
      try {
        setLoading(true);
        const [
          branchesRes,
          departmentsRes,
          designationsRes,
          gradesRes,
          categoriesRes,
          subDepartmentsRes,
        ] = await Promise.all([
          branchService.getBranches(),
          departmentService.getDepartments(),
          designationService.getDesignations(),
          gradesService.getGrades(),
          categoryService.getAllCategories(),
          subDepartmentService.getAllSubDepartments(),
        ]);

        if (branchesRes.success) setBranches(branchesRes.data);
        if (departmentsRes.success) setDepartments(departmentsRes.data);
        if (designationsRes.success) setDesignations(designationsRes.data);
        if (gradesRes.success) setGrades(gradesRes.data);
        if (categoriesRes.success) setCategories(categoriesRes.data);
        if (subDepartmentsRes.success)
          setSubDepartments(subDepartmentsRes.data);
      } catch (error) {
        console.error("Error fetching master data:", error);
        toast.error("Failed to fetch master data");
      } finally {
        setLoading(false);
      }
    };

    const fetchEmployees = async () => {
      try {
        const fetchParams = getFetchParams();
        const response = await employeeService.getEmployees(
          Object.keys(fetchParams).length > 0 ? fetchParams : { excludeEmploymentTypes: "External,External" }
        );
        applyResponseToEmployees(response);
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("Failed to fetch employees");
        setEmployees([]);
      }
    };

    // For Payroll Reports we rely on the shared Filters popup to fetch and provide
    // the already-filtered employee list, so we can use employees as-is here.
    const filteredEmployees = employees;

    const handleFilterChange = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
      setFilters({ ...defaultFilters });
      try {
        localStorage.removeItem(PAYROLL_REPORTS_FILTERS_KEY);
      } catch (e) {
        /* ignore */
      }
      fetchEmployees(); // Refetch all employees when clearing filters
    };

    const hasActiveFilters = useMemo(() => {
      const f = filters;
      return !!(
        f.branch || f.department || f.designation || f.grade ||
        f.category || f.subDepartment || f.employmentType || f.status ||
        f.dateFrom || f.dateTo || f.reportDateFrom || f.reportDateTo ||
        (f.dateFilterType && f.dateFilterType !== "all") ||
        f.pfApplicable || f.esiApplicable || f.ptApplicable ||
        f.gratuityApplicable || f.epsApplicable || f.ctcFrom || f.ctcTo
      );
    }, [filters]);

    const exportReport = (reportType) => {
      try {
        // Helper function to get name by ID from master data
        const getNameById = (id, masterDataArray) => {
          if (!id || !masterDataArray) return "";
          const item = masterDataArray.find(
            (item) => item.id === id || item.id === parseInt(id)
          );
          return item ? item.name : "";
        };

        // Helper function to format dates
        const formatDateForExport = (date) => {
          if (!date) return "";
          try {
            return new Date(date).toLocaleDateString("en-GB"); // dd/mm/yyyy format
          } catch (error) {
            return "";
          }
        };

        // For Payroll Reports, use basic export data
        const exportData = filteredEmployees.map((employee, index) => {
          return {
            "Sl No": index + 1,
            "Employee Code": employee.empCode || "",
            Name: employee.user?.name || employee.name || "",
            Department:
              departments.find((d) => d.id === employee.departmentId)?.name ||
              "",
            Designation:
              designations.find((d) => d.id === employee.designationId)?.name ||
              "",
            Status: employee.employmentStatus || "Active",
            Branch:
              branches.find((b) => b.id === employee.branchId)?.name || "",
            Grade: grades.find((g) => g.id === employee.gradeId)?.name || "",
            Category:
              categories.find((c) => c.id === employee.categoryId)?.name || "",
            "Employment Type": employee.employmentType || "",
            Email: employee.user?.email || employee.email || "",
            Phone: employee.user?.phone || employee.phone || "",
            "Date of Birth": formatDateForExport(employee.user?.dateOfBirth),
            Gender: employee.user?.gender || employee.gender || "",
            Address: employee.user?.address || employee.address || "",
            City: employee.user?.city || employee.city || "",
            State: employee.user?.state || employee.state || "",
            Pincode: employee.user?.pincode || employee.pincode || "",
            "Aadhaar Number":
              employee.user?.aadhaarNumber || employee.aadhaarNumber || "",
            "PAN Number": employee.user?.panNumber || employee.panNumber || "",
            "Blood Group":
              employee.user?.bloodGroup || employee.bloodGroup || "",
            "Emergency Contact":
              employee.user?.emergencyContact ||
              employee.emergencyContact ||
              "",
            "Emergency Phone":
              employee.user?.emergencyPhone || employee.emergencyPhone || "",
          };
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(exportData);

        // Set column widths to auto-fit based on content
        const colWidths = Object.keys(exportData[0] || {}).map((key, index) => {
          const maxLength = Math.max(
            key.length, // Header length
            ...exportData.map((row) => String(row[key] || "").length) // Content length
          );
          return { wch: Math.min(Math.max(maxLength + 2, 10), 50) }; // Min 10, Max 50
        });
        worksheet["!cols"] = colWidths;

        // Style all cells with font and vertical alignment
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let row = range.s.r; row <= range.e.r; row++) {
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            if (!worksheet[cellAddress]) continue;

            const isHeader = row === 0;

            // Set cell style
            worksheet[cellAddress].s = {
              font: {
                name: "Segoe UI",
                sz: 10,
                bold: isHeader,
                color: { rgb: "000000" },
              },
              fill: isHeader
                ? {
                    patternType: "solid",
                    fgColor: { rgb: "F0F0F0" },
                  }
                : undefined,
              alignment: {
                horizontal: "left",
                vertical: "center",
                wrapText: true,
              },
            };
          }
        }

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, reportType);

        const now = new Date();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const yyyy = now.getFullYear();
        let companyCode = "COMPANY";
        try {
          const raw = localStorage.getItem("companyData");
          const companyData = raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : null;
          const code = String(companyData?.code || "")
            .trim()
            .replace(/[^A-Za-z0-9]/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_+|_+$/g, "")
            .toUpperCase();
          if (code) companyCode = code;
        } catch (_) {
          console.log("Error getting company code:", _);
        }
        const safeReportName = String(reportType || "Payroll_Report")
          .replace(/[^A-Za-z0-9]+/g, "_")
          .replace(/_+/g, "_")
          .replace(/^_+|_+$/g, "");
        const filename = `${companyCode}_${safeReportName}_${mm}_${yyyy}.xlsx`;

        // Write and download
        XLSX.writeFile(workbook, filename);

        toast.success(`${reportType} downloaded successfully!`);
      } catch (error) {
        console.error("Export error:", error);
        toast.error("Failed to download report. Please try again.");
      }
    };

    // Filter popup uses shared Filters component

    return (
      <div className="flex-1 overflow-auto">
        <style>{`
          @keyframes blink-border {
            0%, 100% { border-color: rgb(59 130 246); box-shadow: 0 0 0 0 rgba(209, 22, 22, 0.7); }
            50% { border-color: rgb(37 99 235); box-shadow: 0 0 0 4px rgba(236, 9, 9, 0); }
          }
        `}</style>
        <div
          className={`max-h-full ${
            darkMode ? "bg-[#1C1C1C] text-[#E0E0E0]" : "bg-white text-gray-900"
          }`}
        >
          <div className="flex h-full">
            {/* Vertical Sidebar */}
            {!hideSidebar && (
              <div
                className={`w-80 border-r ${
                  darkMode
                    ? "bg-[#2C2C2C] border-[#3C3C3C]"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
              <div className="p-6">
                <h1
                  className={`text-2xl font-bold mb-8 ${
                    darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                  }`}
                >
                  Payroll Reports
                </h1>

                {/* Sub-tabs for payroll reports */}
                <div className="space-y-1">
                  {/* <h3
                    className={`text-sm font-semibold mb-3 ${
                      darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                    }`}
                  >
                    PAYROLL REPORTS
                  </h3> */}
                  {[
                    { id: "payslips", label: "Payslips", icon: FileText },
                    // { id: "form-16", label: "Form-16", icon: FileText },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveSubTab(tab.id)}
                      className={`w-full flex items-center px-4 py-2 rounded-none text-left text-sm transition-colors ${
                        activeSubTab === tab.id
                          ? `${
                              darkMode
                                ? "bg-[#3C3C3C] text-[#BB86FC]"
                                : "bg-redhalo-mint/40 text-redhalo-navy"
                            }`
                          : `${
                              darkMode
                                ? "text-[#A6A9C8] hover:bg-[#3C3C3C]"
                                : "text-gray-600 hover:bg-gray-100"
                            }`
                      }`}
                    >
                      <tab.icon size={16} className="mr-3" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              </div>
            )}

            {/* Main Content Area */}
            <div className={`flex-1 ${hideSidebar ? "p-4" : "p-6"}`}>
              {/* Report Content */}
              <div
                className={`rounded-none shadow-lg ${
                  darkMode
                    ? "bg-[#2C2C2C] border border-[#3C3C3C]"
                    : "bg-white border border-gray-200"
                }`}
              >
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2
                        className={`text-2xl font-bold ${
                          darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                        }`}
                      >
                        {[
                          { id: "payslips", label: "Payslips" },
                          { id: "form-16", label: "Form-16" },
                        ].find((tab) => tab.id === activeSubTab)?.label ||
                          "Payroll Report"}
                      </h2>
                      <p
                        className={`text-sm mt-1 ${
                          darkMode ? "text-[#A6A9C8]" : "text-gray-600"
                        }`}
                      >
                        Generate and download payroll reports
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowFilterPopup(true)}
                        className={`flex items-center px-4 py-2 rounded-none font-medium transition-colors border ${
                          darkMode
                            ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } ${hasActiveFilters ? "border-red-600" : darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}
                        style={hasActiveFilters ? { animation: "blink-border 1.5s ease-in-out infinite" } : {}}
                      >
                        <Filter size={18} className="mr-2" />
                        Filters
                      </button>
                      {/* Load Data button removed: Filters popup already fetches and returns the filtered employee list */}
                    </div>
                  </div>
                </div>
 
                {/* Payroll Reports Content */}
                <div className="p-6 min-h-[420px]">
                  {activeSubTab === "payslips" && (
                    <Payslips
                      employees={filteredEmployees}
                      darkMode={darkMode}
                      hasActiveFilters={hasActiveFilters}
                      selectedMonthYear={selectedMonthYear}
                      onMonthYearChange={setSelectedMonthYear}
                    />
                  )}
                  {activeSubTab === "form-16" && (
                    <Form16 employees={filteredEmployees} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Popup - minimal Filters usage */}
        {showFilterPopup && (
          <Filters
            payslipMonthYear={activeSubTab === "payslips" && selectedMonthYear ? selectedMonthYear : undefined}
            onApply={(employeesList, appliedFilters) => {
              if (Array.isArray(employeesList)) {
                setEmployees(employeesList);
              }
              if (appliedFilters) {
                const nextFilters = { ...defaultFilters, ...appliedFilters };
                setFilters(nextFilters);
                try {
                  localStorage.setItem(PAYROLL_REPORTS_FILTERS_KEY, JSON.stringify(nextFilters));
                } catch (e) {
                  /* ignore */
                }
              }
              setShowFilterPopup(false);
            }}
            onClose={() => setShowFilterPopup(false)}
            onClear={clearFilters}
            darkMode={darkMode}
            initialFilters={filters}
          />
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.darkMode === nextProps.darkMode;
  }
);

export default PayrollReports;
